import { ref } from 'vue'
import {
  useRoute,
  useRouter,
  type LocationQueryRaw,
  type RouteLocationNormalizedLoaded,
  type Router,
} from 'vue-router'
import { reservationApi } from '@/api/reservation'
import { useAuthStore } from '@/stores/auth'
import { parseApiError } from '@/utils/errorHandler'

type RouteLike = Pick<RouteLocationNormalizedLoaded, 'path' | 'query'>

interface ReservationGuardContext {
  authStore: ReturnType<typeof useAuthStore>
  router: Router
  route: RouteLike
}

interface ClearReservationOptions {
  clearDrawer?: boolean
  clearTableNo?: boolean
}

interface ValidateReservationOptions extends ClearReservationOptions {
  mode?: 'new' | 'old'
  skipAuth?: boolean
  bearerToken?: string
}

interface ReservationGuardResult<T = unknown> {
  ok: boolean
  message: string
  response?: T
}

function normalizeGenericError(error: unknown, fallback: string): string {
  const message = parseApiError(error).message?.trim()
  return message || fallback
}

function normalizeActiveReservationError(error: unknown): string {
  const message = parseApiError(error).message?.trim()
  const normalized = message?.toLowerCase() ?? ''

  if (
    !message ||
    normalized.includes('request failed with status code 422') ||
    normalized.includes('session is expired') ||
    normalized.includes('ordering session is expired') ||
    normalized.includes('reservation is expired') ||
    normalized.includes('reservation has expired') ||
    normalized.includes('no active reservation') ||
    normalized.includes('reservation not found') ||
    normalized.includes('inactive reservation') ||
    normalized.includes('reservation is not active')
  ) {
    return 'This dining session is no longer active. Please scan the table QR or join the table again to continue ordering.'
  }

  return message
}

function buildCleanQuery(
  query: RouteLike['query'],
  options: ClearReservationOptions = {}
): LocationQueryRaw {
  const nextQuery = { ...(query as LocationQueryRaw) }
  delete nextQuery.reservation_id
  if (options.clearDrawer) delete nextQuery.drawer
  if (options.clearTableNo) delete nextQuery.table_no
  return nextQuery
}

export async function clearReservationFromUrlAndStateWithContext(
  context: ReservationGuardContext,
  options: ClearReservationOptions = {}
): Promise<void> {
  context.authStore.clearActiveReservation()

  const hasReservationId = 'reservation_id' in (context.route.query as Record<string, unknown>)
  const hasDrawer =
    options.clearDrawer && 'drawer' in (context.route.query as Record<string, unknown>)
  const hasTableNo =
    options.clearTableNo && 'table_no' in (context.route.query as Record<string, unknown>)

  if (!hasReservationId && !hasDrawer && !hasTableNo) return

  await context.router.replace({
    path: context.route.path,
    query: buildCleanQuery(context.route.query, options),
  })
}

export async function validateReservationStillActiveWithContext(
  context: ReservationGuardContext,
  activeReservationId: number,
  options: ValidateReservationOptions = {}
): Promise<ReservationGuardResult> {
  try {
    const response =
      options.mode === 'old'
        ? await reservationApi.checkActiveReservation(
            { active_reservation_id: activeReservationId },
            {
              ...(options.skipAuth === true ? { skipAuth: true } : {}),
              ...(options.bearerToken ? { bearerToken: options.bearerToken } : {}),
            }
          )
        : await reservationApi.checkActiveReservationNew(
            { active_reservation_id: activeReservationId },
            {
              ...(options.skipAuth === true ? { skipAuth: true } : {}),
              ...(options.bearerToken ? { bearerToken: options.bearerToken } : {}),
            }
          )

    const code =
      (response as { code?: number })?.code ??
      (response as { data?: { code?: number } })?.data?.code
    const ok =
      options.mode === 'old'
        ? !(response as { error?: unknown }).error
        : !(response as { error?: unknown }).error && (code === undefined || code === 200)

    if (!ok) {
      await clearReservationFromUrlAndStateWithContext(context, options)
      return {
        ok: false,
        message: normalizeActiveReservationError(response),
        response,
      }
    }

    return {
      ok: true,
      message: '',
      response,
    }
  } catch (error) {
    await clearReservationFromUrlAndStateWithContext(context, options)
    return {
      ok: false,
      message: normalizeActiveReservationError(error),
    }
  }
}

export async function validateReservationHostAccess(
  activeReservationId: number
): Promise<ReservationGuardResult> {
  try {
    const response = await reservationApi.checkReservationHost({
      active_reservation_id: activeReservationId,
    })
    return {
      ok: !(response as { error?: unknown }).error,
      message: (response as { error?: unknown }).error
        ? normalizeGenericError(response, 'You no longer have access to this reservation.')
        : '',
      response,
    }
  } catch (error) {
    return {
      ok: false,
      message: normalizeGenericError(error, 'You no longer have access to this reservation.'),
    }
  }
}

export function useReservationSessionGuard() {
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const isValidatingReservation = ref<boolean>(false)

  const context: ReservationGuardContext = {
    authStore,
    router,
    route,
  }

  const validateReservationStillActive = async (
    activeReservationId: number,
    options?: ValidateReservationOptions
  ): Promise<ReservationGuardResult> => {
    isValidatingReservation.value = true
    try {
      return await validateReservationStillActiveWithContext(context, activeReservationId, options)
    } finally {
      isValidatingReservation.value = false
    }
  }

  return {
    isValidatingReservation,
    clearReservationFromUrlAndState: (options?: ClearReservationOptions) =>
      clearReservationFromUrlAndStateWithContext(context, options),
    validateReservationStillActive,
    validateReservationHostAccess,
  }
}
